import { answeredByServer, createConnectivity } from "@thekrystalship/auth-client/connectivity";
import { entryNode, homeConn } from "./config.js";
import { originOf } from "./oidc.js";

// connectivity.js — whether the panel reaches its entry node: the one flag the shell draws its offline
// state by. How it is measured and kept true is `@thekrystalship/auth-client/connectivity`'s; this
// module names the node and the address it is asked at.
//
// The node is the entry node (`config.entryNode()`), else the node serving the panel (`homeConn()`):
// the one the panel talks to first, so without it there is no roster, no session spent and nothing to
// draw. Another member that does not answer is that member's state, said on its own surfaces by the
// per-node reach and capability model, and never makes the panel offline.
//
// The probe is the node's `/health`, which kgsm-api answers at its origin with no credential. Every
// request the session sends (`sessionStore`'s `fetch`, the one funnel under `apiClient` and
// `authorized()`) reports its outcome through `observe` when it is addressed to that node.

/** The origin of the node the panel reaches first, or "" with no node at all. */
function entryOrigin() {
  const entry = entryNode();
  if (entry) return entry;
  const home = homeConn();
  return home ? originOf(home.url) : "";
}

export const connectivity = createConnectivity({
  probe: () => {
    const origin = entryOrigin();
    return origin ? origin + "/health" : "";
  },
});

/** Starts listening for the network coming and going, and measures once. Answers the way to stop. */
export const startConnectivity = () => connectivity.start();

function addressedToEntry(input) {
  const origin = entryOrigin();
  if (!origin) return false;
  try {
    const url = typeof input === "string" ? input : input && input.url ? input.url : String(input);
    return new URL(url, globalThis.location ? globalThis.location.href : undefined).origin === origin;
  } catch {
    return false;
  }
}

// A request that never went out: aborted by its caller, or held back because there is no session.
const notSent = (e) => !!e && (e.name === "AbortError" || e.code === "ended" || e.code === "none");

/**
 * Reports what a request to the entry node came to, and answers what it did. A response is the node
 * answering unless a gateway or a portal stood in for it (`answeredByServer`); a transport failure is
 * not; a request that was never sent says nothing.
 * @param {RequestInfo | URL} input
 * @param {Promise<Response>} sent
 * @returns {Promise<Response>}
 */
export function observe(input, sent) {
  if (!addressedToEntry(input)) return sent;
  return sent.then(
    (res) => { connectivity.report(answeredByServer(res)); return res; },
    (e) => { if (!notSent(e)) connectivity.report(false); throw e; },
  );
}
